/**
 * The QuestionType a survey question is — i.e. the BE `_type` discriminator on
 * `SurveyPart` (see `Levitate.Api.Models/Surveys/Survey.cs`).
 *
 * The question-type dropdown selects a QuestionType, NOT a `FormFieldType` — a
 * QuestionType uniquely identifies the question, whereas a `FormFieldType` does
 * not (two QuestionTypes can share one backing `FormFieldType`). The backing
 * `FormFieldType` is *derived* from the QuestionType for persistence.
 *
 * Adding a new question type = one entry in `QUESTION_TYPES`.
 */
import { FormFieldType } from '@ViewModels';

/**
 * QuestionType values are the BE `_type` discriminator strings. Declared as a
 * `const` object (not a TS `enum`) so call sites get dot-notation (`QuestionType.DropDown`)
 * while the derived type stays a string-literal union — which is what the SDK
 * `SurveyQuestion._type` fields are typed as, so values assign with no casts.
 */
export const QuestionType = {
	ShortAnswer: 'ShortAnswerQuestion',
	LongAnswer: 'LongAnswerQuestion',
	DropDown: 'DropDownQuestion',
	CheckBoxes: 'CheckBoxesQuestion',
	FileUpload: 'FileUploadQuestion',
	LinearScale: 'LinearScaleQuestion',
	NPS: 'NpsQuestion',
} as const;

export type QuestionType = (typeof QuestionType)[keyof typeof QuestionType];

interface QuestionTypeInfo {
	/** The `_type` discriminator — the unique identity of the question. */
	type: QuestionType;
	/** User-facing label shown in the question-type dropdown. */
	label: string;
	/** The `FormFieldType` that backs this question (for the CustomForm field). */
	fieldType: FormFieldType;
}

/** Ordered list driving the dropdown. Order = display order. */
const QUESTION_TYPES: QuestionTypeInfo[] = [
	{ type: QuestionType.ShortAnswer, label: 'Short Answer', fieldType: FormFieldType.String },
	// Long Answer is uniquely backed by `Text` (Short Answer uses `String`).
	{ type: QuestionType.LongAnswer, label: 'Long Answer', fieldType: FormFieldType.Text },
	{ type: QuestionType.DropDown, label: 'Dropdown', fieldType: FormFieldType.Option },
	{ type: QuestionType.CheckBoxes, label: 'Checkboxes', fieldType: FormFieldType.MultipleOptions },
	{ type: QuestionType.FileUpload, label: 'File Upload', fieldType: FormFieldType.FileUpload },
	// Both scale types map to `Integer`; LinearScale is first so it's the default an untyped Integer resolves to.
	{ type: QuestionType.LinearScale, label: 'Linear Scale', fieldType: FormFieldType.Integer },
	{ type: QuestionType.NPS, label: 'NPS', fieldType: FormFieldType.Integer },
];

const byType = new Map<QuestionType, QuestionTypeInfo>(QUESTION_TYPES.map(q => [q.type, q]));

// First QuestionType for a given FormFieldType wins — the default a field resolves
// to when only its `fieldType` is known (legacy fields with no explicit `_type`).
const byFieldType = new Map<FormFieldType, QuestionType>();
for (const q of QUESTION_TYPES) {
	if (!byFieldType.has(q.fieldType)) {
		byFieldType.set(q.fieldType, q.type);
	}
}

/** Type guard: is `value` a known QuestionType? */
const isQuestionType = (value: unknown): value is QuestionType =>
	typeof value === 'string' && byType.has(value as QuestionType);

/** The `FormFieldType` that backs a QuestionType. */
export const fieldTypeForQuestionType = (type: QuestionType): FormFieldType =>
	byType.get(type)?.fieldType ?? FormFieldType.String;

/** The default QuestionType for a `FormFieldType` (used when a field carries no `_type`). */
export const questionTypeForFieldType = (fieldType: FormFieldType): QuestionType =>
	byFieldType.get(fieldType) ?? QuestionType.ShortAnswer;

/** Dropdown options ({ title, value }) for the given QuestionTypes, preserving order. */
export const questionTypeOptions = (types: QuestionType[]): Array<{ title: string; value: QuestionType }> =>
	types.map(type => ({ title: byType.get(type)?.label ?? '', value: type }));

/** Dropdown options derived from a legacy `supportedFieldTypes` list (1:1 to QuestionTypes). */
export const questionTypeOptionsFromFieldTypes = (
	fieldTypes: FormFieldType[]
): Array<{ title: string; value: QuestionType }> => questionTypeOptions(fieldTypes.map(questionTypeForFieldType));

/**
 * Resolve the QuestionType a field/question currently represents — used to highlight
 * the right dropdown option. Prefers an explicit, valid `_type` (V2 questions carry
 * one); otherwise derives it from the backing `FormFieldType`.
 */
export const currentQuestionType = (field: { _type?: unknown; fieldType?: FormFieldType }): QuestionType =>
	isQuestionType(field._type) ? field._type : questionTypeForFieldType(field.fieldType ?? FormFieldType.String);
